import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U2106';
import { doGetAiUsage, luHandleAppiError, type U2106AiUsageResult } from '../U2106Service';

export type UseU2106AiUsageHandlersReturn = {
  loading: boolean;
  aiUsage: U2106AiUsageResult | null;
  loadAiUsage: () => Promise<U2106AiUsageResult | null>;
};

export function useU2106AiUsageHandlers(): UseU2106AiUsageHandlersReturn {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation(NS);
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [aiUsage, setAiUsage] = useState<U2106AiUsageResult | null>(null);

  const loadAiUsage = useCallback(async (): Promise<U2106AiUsageResult | null> => {
    setLoading(true);
    try {
      const response = await doGetAiUsage(axios);
      const result = response.data.result;
      setAiUsage(result);
      return result;
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else {
        toast.error(t('myAiUsage.errors.generic', { ns: NS }));
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [axios, t, toast]);

  return { loading, aiUsage, loadAiUsage };
}
